/**
 * `backtest` namespace — Korean translations.
 *
 * Mirrors `en/backtest.ts` exactly; the `Record<BacktestKey, string>` annotation
 * makes a missing or misspelled key a compile error, so this file cannot drift
 * from the canonical English source. Tone: concise 명사형/합니다체 for descriptions;
 * UI chrome avoids 해요체. Glossary is binding — Backtest→백테스트, Buy→매수,
 * Sell→매도, Return→수익률, Fee→수수료, Rule→규칙; CAGR / bps / Fear & Greed / FGI
 * stay English.
 */
import type { BacktestKey } from "../en/backtest";

export const backtest: Record<BacktestKey, string> = {
  // ── panel chrome ──
  panelTitle: "백테스트",
  panelSubtitle: "과거 시세 데이터로 이 전략을 시뮬레이션합니다.",

  // ── locked (trading feature gate) ──
  lockedTitle: "Toss 연결 필요",
  lockedDesc:
    "백테스트는 Toss 연결을 통해 과거 시세 데이터를 읽습니다. 백테스트를 실행하려면 Toss를 연결하세요.",
  lockedCta: "Toss 연결",

  // ── config form ──
  configTitle: "새 백테스트",
  periodLabel: "기간",
  period6m: "6개월",
  period1y: "1년",
  period3y: "3년",
  periodAll: "전체",
  periodCustom: "직접 설정",
  fromLabel: "시작일",
  toLabel: "종료일",
  initialCashLabel: "초기 자본",
  currencyLabel: "통화",
  advancedLabel: "고급",
  feeBpsLabel: "수수료 (bps)",
  slippageBpsLabel: "슬리피지 (bps)",
  bpsHint: "1 bp = 0.01%",
  useAdvisorLabel: "매 트리거마다 AI 어드바이저 실행",
  useAdvisorHint: "트리거마다 모델을 호출합니다 — 더 느리고 크레딧을 사용합니다.",
  runButton: "백테스트 실행",
  runningButton: "대기열 등록 중…",
  invalidCash: "0보다 큰 금액을 입력하세요.",
  invalidRange: "시작일은 종료일보다 앞서야 합니다.",
  conflictNotice: "이 전략의 백테스트가 이미 실행 중입니다. 완료된 후 다시 실행하세요.",
  runError: "백테스트를 시작하지 못했습니다.",

  // ── job status ──
  statusQueued: "대기",
  statusRunning: "실행 중",
  runningTitle: "백테스트 실행 중…",
  runningDesc: "과거 데이터를 불러와 전략을 재생하고 있습니다. 잠시 걸릴 수 있습니다.",
  errorTitle: "백테스트 실패",
  detailLoadError: "이 백테스트를 불러오지 못했습니다.",
  noResults: "기간을 설정하고 백테스트를 실행하면 결과가 표시됩니다.",

  // ── result metrics ──
  resultsTitle: "결과",
  metricInitialCash: "시작금액",
  metricFinalEquity: "최종 평가금액",
  metricTotalReturn: "총수익률",
  metricCagr: "CAGR",
  metricMaxDrawdown: "최대 낙폭",
  metricWinRate: "승률",
  metricPayoffRatio: "손익비",
  metricTradeCount: "거래 횟수",
  cagrTooShort: "기간 1년 미만",
  benchmarkValue: "벤치마크 {value}",
  roundTripsCaption: "왕복 {count}회",

  // ── equity chart ──
  equityTitle: "자산 곡선",
  equitySubtitle: "전략 vs 벤치마크",
  legendStrategy: "전략",
  legendBenchmark: "벤치마크",
  noEquity: "이 실행에 대한 자산 데이터가 없습니다.",

  // ── price overlay chart ──
  priceTitle: "가격 오버레이",
  priceSubtitle: "시작 시점 100 기준 — 겹쳐 볼 항목을 선택하세요",
  priceFgi: "FGI",
  priceTrades: "거래",
  priceRebasedNote:
    "각 선은 첫날을 100으로 환산해, 가격대가 다른 자산도 하나의 % 축에서 비교됩니다. FGI는 오른쪽 축(0–100)에 표시됩니다.",
  priceEmpty: "이 실행에 대한 가격 데이터가 없습니다.",
  priceUnchartedTrades: "차트에 표시되지 않은 거래 {count}건 (가격 데이터 없음: {symbols}).",
  priceIndicatorNote:
    "지표는 표시된 구간에서 계산됩니다. SMA/EMA는 가격 축에 겹쳐 표시되고, RSI/ROC/낙폭은 별도 축에 표시되며, 구간 시작 부근의 값은 근사치입니다.",

  // ── advisor decisions ──
  advisorTitle: "어드바이저 판단",
  advisorEmpty: "이 실행에서 어드바이저 판단이 없습니다.",
  advisorVerdictApprove: "승인",
  advisorVerdictVeto: "거부",
  advisorVerdictResize: "크기 조정",
  advisorResizeMultiplier: "×{value}",
  advisorShowMore: "더 보기",
  advisorShowLess: "접기",

  // ── data coverage ──
  coverageTitle: "데이터 커버리지",
  coverageRange: "실제 구간 {from} ~ {to}",
  warnDataStartsLater:
    "요청한 시작일보다 시세 데이터가 늦게 시작되어, 데이터가 있는 첫날부터 백테스트가 시작됩니다.",
  warnNoData: "요청한 구간에 사용할 수 있는 시세 데이터가 없습니다.",
  warnNoFgiData:
    "이 구간 일부에 Fear & Greed 데이터가 없어, 해당 기간에는 FGI를 사용하는 규칙이 건너뛰어졌습니다.",
  warnGeneric: "커버리지 경고: {code}",

  // ── trades table ──
  tradesTitle: "거래 내역",
  tradesEmpty: "이 백테스트에서는 거래가 발생하지 않았습니다.",
  colDate: "날짜",
  colSymbol: "종목",
  colSide: "매수/매도",
  colQty: "수량",
  colPrice: "가격",
  colFee: "수수료",
  colRule: "규칙",
  sideBuy: "매수",
  sideSell: "매도",
  tradesShown: "{total}건 중 {shown}건 표시",
  showAllTrades: "전체 거래 보기",

  // ── recent runs ──
  recentTitle: "최근 백테스트",
  recentEmpty: "아직 백테스트가 없습니다. 위에서 실행해 보세요.",
  recentError: "최근 백테스트를 불러오지 못했습니다.",
  reopenAria: "{date}에 실행한 백테스트 열기",
  periodRange: "{from} ~ {to}",

  // ── delete a run ──
  deleteRun: "삭제",
  deleteConfirm: "정말 삭제할까요?",
  deleting: "삭제 중…",
  deleteError: "이 실행을 삭제하지 못했습니다.",
};
